import { defineStore } from 'pinia'
import { computed, shallowRef } from 'vue'
import type { AppHttpError } from '~/shared-kernel/domain/http-error'
import type { AuthenticatedSessionView, PasswordRotationRequiredView, PrincipalView } from '~/shared-kernel/domain/auth'
import { BFF_ERROR_CODES } from '~/shared-kernel/infrastructure/bff/error-codes'
import type { CustomerGoogleRegistrationPayload, CustomerRegistrationFormOptions, CustomerRegistrationPayload } from '~/modules/customer/domain/customer-registration'
import {
  customerGoogleRegister,
  customerLogin,
  customerLogout,
  customerRegister,
  getCustomerRegistrationFormOptions,
  getCustomerMe,
  getGoogleRegistrationProfile,
  updateCustomerProfile
} from '~/modules/customer/infrastructure/customer-auth.gateway'
import { setCustomerCartOwner } from '~/shared-kernel/infrastructure/session/customer-cart-scope'

function hasResolvedCustomerIdentity(principal: PrincipalView | null) {
  return Boolean(principal && 'identityRequirement' in principal)
}

type AuthenticationFailure = 'terminal' | 'refresh-superseded'

function classifyAuthenticationFailure(error: unknown): AuthenticationFailure | null {
  if (typeof error !== 'object' || error === null) return null
  const candidate = error as Partial<AppHttpError>
  if (candidate.status === 401 || candidate.status === 403) return 'terminal'
  if (candidate.status === 409 && candidate.code === BFF_ERROR_CODES.refreshSuperseded) {
    return 'refresh-superseded'
  }
  return null
}

export const useCustomerSessionStore = defineStore('customer-session', () => {
  const principal = shallowRef<PrincipalView | null>(null)
  const passwordRotation = shallowRef<PasswordRotationRequiredView | null>(null)
  const loading = shallowRef(false)
  const error = shallowRef<AppHttpError | null>(null)
  const registrationFormOptions = shallowRef<CustomerRegistrationFormOptions | null>(null)
  const registrationAccepted = shallowRef(false)
  const isAuthenticated = computed(() => Boolean(principal.value))
  const hasResolvedIdentityState = computed(() => hasResolvedCustomerIdentity(principal.value))
  let principalRequest: Promise<PrincipalView | null> | null = null
  let sessionGeneration = 0

  function acceptSession(session: AuthenticatedSessionView) {
    sessionGeneration += 1
    principalRequest = null
    principal.value = session.principal
    passwordRotation.value = null
  }

  function clear() {
    sessionGeneration += 1
    principalRequest = null
    principal.value = null
    passwordRotation.value = null
    error.value = null
  }

  async function discardInvalidSession(requestError: unknown) {
    const failure = classifyAuthenticationFailure(requestError)
    if (!failure) return false

    if (import.meta.client && failure === 'refresh-superseded') {
      try {
        await customerLogout()
      } catch {
        // Local state still has to converge to signed out if cookie cleanup is unavailable.
      }
    }
    clear()
    return true
  }

  async function login(loginIdentifier: string, password: string) {
    loading.value = true
    error.value = null
    try {
      const result = await customerLogin({ loginIdentifier, password })
      if (result.authStatus === 'password_rotation_required') {
        passwordRotation.value = result
        return result
      }
      acceptSession(result)
      return result
    } catch (requestError) {
      error.value = requestError as AppHttpError
      throw requestError
    } finally {
      loading.value = false
    }
  }

  async function loadRegistrationFormOptions() {
    error.value = null
    try {
      registrationFormOptions.value = await getCustomerRegistrationFormOptions()
      return registrationFormOptions.value
    } catch (requestError) {
      error.value = requestError as AppHttpError
      throw requestError
    }
  }

  async function register(payload: CustomerRegistrationPayload) {
    loading.value = true
    error.value = null
    registrationAccepted.value = false
    try {
      await customerRegister(payload)
      registrationAccepted.value = true
    } catch (requestError) {
      error.value = requestError as AppHttpError
      throw requestError
    } finally {
      loading.value = false
    }
  }

  async function registerWithGoogle(payload: CustomerGoogleRegistrationPayload) {
    loading.value = true
    error.value = null
    try {
      const result = await customerGoogleRegister(payload)
      acceptSession(result)
      return result.returnTo
    } catch (requestError) {
      error.value = requestError as AppHttpError
      throw requestError
    } finally {
      loading.value = false
    }
  }

  function loadGoogleRegistrationProfile() {
    return getGoogleRegistrationProfile()
  }

  function requestPrincipal(options: { background?: boolean } = {}) {
    if (!principalRequest) {
      const request = getCustomerMe(options).finally(() => {
        if (principalRequest === request) principalRequest = null
      })
      principalRequest = request
    }

    return principalRequest
  }

  async function hydrate() {
    if (hasResolvedIdentityState.value) return
    const generation = sessionGeneration
    try {
      const resolvedPrincipal = await requestPrincipal()
      if (generation !== sessionGeneration) return
      if (!resolvedPrincipal) return clear()
      principal.value = resolvedPrincipal
      setCustomerCartOwner(resolvedPrincipal.id)
    } catch (requestError) {
      if (generation !== sessionGeneration) return
      if (await discardInvalidSession(requestError)) return

      throw requestError
    }
  }

  async function refreshPrincipal() {
    const generation = sessionGeneration
    try {
      const resolvedPrincipal = await requestPrincipal({ background: true })
      if (generation !== sessionGeneration) return principal.value
      if (!resolvedPrincipal) {
        clear()
        return null
      }
      principal.value = resolvedPrincipal
      setCustomerCartOwner(resolvedPrincipal.id)
      return resolvedPrincipal
    } catch (requestError) {
      if (generation !== sessionGeneration) return principal.value
      await discardInvalidSession(requestError)
      throw requestError
    }
  }

  async function updateProfile(displayName: string) {
    principal.value = await updateCustomerProfile({ displayName })
  }

  async function logout() {
    sessionGeneration += 1
    principalRequest = null
    try {
      await customerLogout()
    } finally {
      clear()
    }
  }

  return {
    principal,
    passwordRotation,
    loading,
    error,
    registrationFormOptions,
    registrationAccepted,
    isAuthenticated,
    hasResolvedIdentityState,
    login,
    loadRegistrationFormOptions,
    register,
    registerWithGoogle,
    loadGoogleRegistrationProfile,
    hydrate,
    refreshPrincipal,
    updateProfile,
    logout,
    clear
  }
})
